import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import EnhancedCard from '../components/EnhancedCard';
import ShareFlexModal from '../components/ShareFlexModal';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, Filter, Flame, Coins, AlertTriangle, Layers, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../providers/AuthProvider';
import { getInventory } from '../utils/inventoryManager';
import { getCardImageById } from '../utils/cardImageHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Burn rewards by rarity
const BURN_REWARDS = {
  common: 10,
  rare: 25,
  epic: 50,
  legendary: 100
};

// Burn All Duplicates Confirmation Modal
const BurnDuplicatesModal = ({ duplicateCount, estimatedShards, onConfirm, onCancel, isBurning }) => {
  return (
    <motion.div
      className="fixed inset-0 z-[900] flex items-center justify-center bg-black/90"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="bg-[#0a0a0a] border-2 border-orange-500 rounded-2xl p-6 max-w-md w-full mx-4"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Trash2 className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h2 className="font-anton text-2xl text-orange-400">BURN ALL DUPLICATES?</h2>
            <p className="font-space text-sm text-white/60">Keep one of each, burn extras</p>
          </div>
        </div>

        <div className="bg-black/40 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="font-space text-sm text-white/60">Cards to burn:</span>
            <span className="font-anton text-xl text-red-400">{duplicateCount}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-space text-sm text-white/60">Estimated $SLAP:</span>
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-400" />
              <span className="font-anton text-xl text-yellow-400">~{estimatedShards}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 p-3 bg-orange-500/10 rounded-lg mb-6">
          <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
          <p className="font-space text-xs text-orange-400">
            This will keep one copy of each character and burn all extras. Cannot be undone!
          </p>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={onCancel}
            className="flex-1 bg-transparent border-2 border-white/20 text-white hover:bg-white/10"
            disabled={isBurning}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isBurning || duplicateCount === 0}
            className="flex-1 bg-orange-600 hover:bg-orange-500 text-white font-anton"
          >
            {isBurning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                BURNING...
              </>
            ) : (
              <>
                <Flame className="w-4 h-4 mr-2" />
                BURN {duplicateCount} CARDS
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const BurnConfirmModal = ({ card, onConfirm, onCancel, isBurning }) => {
  const reward = BURN_REWARDS[card?.rarity] || 10;
  
  return (
    <motion.div
      className="fixed inset-0 z-[900] flex items-center justify-center bg-black/90"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="bg-[#0a0a0a] border-2 border-red-500 rounded-2xl p-6 max-w-md w-full mx-4"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <Flame className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h2 className="font-anton text-2xl text-red-500">BURN CARD?</h2>
            <p className="font-space text-sm text-white/60">This action cannot be undone!</p>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="w-24 h-32 rounded-lg overflow-hidden border-2 border-red-500/50">
            <img
              src={getCardImageById(card)}
              alt={card?.character_name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = getCardImageById({ ...card, image_base64: null });
              }}
            />
          </div>
          <div className="flex-1">
            <h3 className="font-anton text-lg text-white">{card?.character_name}</h3>
            <p className="font-bebas text-sm text-white/60">{card?.title}</p>
            <p className="font-space text-xs uppercase mt-2 text-red-400">{card?.rarity}</p>
            
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="font-space text-xs text-yellow-400 mb-1">YOU WILL RECEIVE:</p>
              <div className="flex items-center gap-2">
                <Coins className="w-5 h-5 text-yellow-400" />
                <span className="font-anton text-2xl text-yellow-400">+{reward}</span>
                <span className="font-space text-sm text-yellow-400">$SLAP</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 p-3 bg-red-500/10 rounded-lg mb-6">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="font-space text-xs text-red-400">
            Burning destroys this card forever. Earn $SLAP to buy more packs!
          </p>
        </div>

        <div className="flex gap-4">
          <Button
            onClick={onCancel}
            className="flex-1 bg-transparent border-2 border-white/20 text-white hover:bg-white/10"
            disabled={isBurning}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isBurning}
            className="flex-1 bg-red-600 hover:bg-red-500 text-white font-anton"
          >
            {isBurning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                BURNING...
              </>
            ) : (
              <>
                <Flame className="w-4 h-4 mr-2" />
                BURN IT
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const GalleryPage = () => {
  const navigate = useNavigate();
  const { walletAddress, isAuthenticated, user, refreshUser } = useAuth();
  const SESSION_ID = walletAddress || 'default_session';
  
  const [figures, setFigures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFigure, setSelectedFigure] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [filterRarity, setFilterRarity] = useState('all');
  const [cardToBurn, setCardToBurn] = useState(null);
  const [isBurning, setIsBurning] = useState(false);
  const [showBurnDuplicatesModal, setShowBurnDuplicatesModal] = useState(false);
  const [stackedView, setStackedView] = useState(true); // Card stacking enabled by default

  // Group cards by character for stacking
  const stackedFigures = useMemo(() => {
    const grouped = {};
    figures.forEach(card => {
      const key = card.character_name;
      if (!grouped[key]) {
        grouped[key] = {
          ...card,
          quantity: 1,
          duplicateIds: [card.id],
          allCards: [card]
        };
      } else {
        grouped[key].quantity += 1;
        grouped[key].duplicateIds.push(card.id);
        grouped[key].allCards.push(card);
      }
    });
    return Object.values(grouped);
  }, [figures]);

  // Calculate duplicate count and estimated shards
  const duplicateStats = useMemo(() => {
    let count = 0;
    let shards = 0;
    stackedFigures.forEach(group => {
      if (group.quantity > 1) {
        const dupeCount = group.quantity - 1;
        count += dupeCount;
        // Estimate shards based on card rarity
        shards += dupeCount * (BURN_REWARDS[group.rarity] || 10);
      }
    });
    return { count, shards };
  }, [stackedFigures]);

  useEffect(() => {
    fetchCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SESSION_ID]);

  const fetchCollection = async () => {
    // Increased timeout to 5 seconds to handle slow connections
    const FAILSAFE_TIMEOUT = 5000;
    
    // Create timeout promise for failsafe
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('TIMEOUT')), FAILSAFE_TIMEOUT)
    );
    
    // Create actual fetch promise
    const fetchPromise = axios.get(`${API}/cards/collection?session_id=${SESSION_ID}`, {
      timeout: FAILSAFE_TIMEOUT + 1000
    });
    
    try {
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      let serverCards = response.data.cards || [];
      
      // FIX: Use inventoryManager as primary source (lightweight, no image data)
      const localInventory = getInventory();
      const serverCardIds = new Set(serverCards.map(c => c.id));
      const uniqueLocalCards = localInventory.filter(c => c.id && !serverCardIds.has(c.id));
      
      const mergedCards = [...serverCards, ...uniqueLocalCards];
      setFigures(mergedCards);
      console.log(`[Gallery] Server: ${serverCards.length}, Local: ${localInventory.length}, Merged: ${mergedCards.length} cards`);
    } catch (error) {
      console.error('Error fetching collection:', error);
      
      // FALLBACK: Use inventoryManager only
      const localInventory = getInventory();
      setFigures(localInventory);
      
      if (localInventory.length > 0) {
        toast.info('Showing cached cards', { duration: 2000 });
      } else {
        toast.warning('Collection loading slowly', {
          description: 'Try refreshing the page',
          duration: 3000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFigureClick = (figure) => {
    setSelectedFigure(figure);
    setShowShareModal(true);
  };

  const handleBurnCard = async () => {
    if (!cardToBurn) return;
    
    setIsBurning(true);
    try {
      const response = await axios.post(
        `${API}/cards/burn?session_id=${SESSION_ID}`,
        { card_id: cardToBurn.id }
      );
      
      toast.success(`Burned ${cardToBurn.character_name}!`, {
        description: `+${response.data.shards_earned} Shards earned`,
        icon: '🔥'
      });
      
      // Refresh data
      await fetchCollection();
      await refreshUser();
      setCardToBurn(null);
    } catch (error) {
      console.error('Error burning card:', error);
      toast.error(error.response?.data?.detail || 'Failed to burn card');
    } finally {
      setIsBurning(false);
    }
  };

  const handleBurnDuplicates = async () => {
    setIsBurning(true);
    try {
      const response = await axios.post(`${API}/cards/burn-duplicates?session_id=${SESSION_ID}`);
      
      if (response.data.cards_burned > 0) {
        toast.success(`Burned ${response.data.cards_burned} duplicates!`, {
          description: `+${response.data.total_shards_earned} Shards earned`,
          icon: '🔥'
        });
      } else {
        toast.info('No duplicates to burn');
      }
      
      // Refresh data
      await fetchCollection();
      await refreshUser();
      setShowBurnDuplicatesModal(false);
    } catch (error) {
      console.error('Error burning duplicates:', error);
      toast.error(error.response?.data?.detail || 'Failed to burn duplicates');
    } finally {
      setIsBurning(false);
    }
  };

  const goBack = () => {
    navigate('/');
  };

  const rarityStats = figures.reduce((acc, fig) => {
    acc[fig.rarity] = (acc[fig.rarity] || 0) + 1;
    return acc;
  }, {});
  
  // Filter based on stacked or flat view
  const displayFigures = stackedView ? stackedFigures : figures;
  const filteredFigures = filterRarity === 'all' 
    ? displayFigures 
    : displayFigures.filter(f => f.rarity === filterRarity);
  
  const rarityColors = {
    common: '#A0A0A0',
    rare: '#00CCFF',
    epic: '#FF00FF',
    legendary: '#FFD700',
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden pb-20">
      {/* Cyberpunk grid */}
      <div 
        className="fixed inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(#00FF9915 1px, transparent 1px),
            linear-gradient(90deg, #00FF9915 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Ambient Glow */}
      <div className="fixed top-1/3 left-1/4 w-[600px] h-[600px] bg-neon-green/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="fixed bottom-1/3 right-1/4 w-[600px] h-[600px] bg-neon-pink/10 rounded-full blur-[150px] pointer-events-none" />

      {/* Burn Confirm Modal */}
      <AnimatePresence>
        {cardToBurn && (
          <BurnConfirmModal
            card={cardToBurn}
            onConfirm={handleBurnCard}
            onCancel={() => setCardToBurn(null)}
            isBurning={isBurning}
          />
        )}
      </AnimatePresence>

      {/* Burn Duplicates Modal */}
      <AnimatePresence>
        {showBurnDuplicatesModal && (
          <BurnDuplicatesModal
            duplicateCount={duplicateStats.count}
            estimatedShards={duplicateStats.shards}
            onConfirm={handleBurnDuplicates}
            onCancel={() => setShowBurnDuplicatesModal(false)}
            isBurning={isBurning}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <header className="relative z-50 pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={goBack}
              className="bg-transparent border-2 border-white/20 text-white hover:border-white hover:bg-white/10 font-space uppercase"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            
            {/* Shards Balance */}
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-black/60 border-2 border-cyan-500/50">
              <Coins className="w-5 h-5 text-cyan-400" />
              <span className="font-anton text-xl text-cyan-400" data-testid="shards-balance">
                {user?.shards?.toLocaleString() || 0}
              </span>
              <span className="font-space text-xs text-white/60">SHARDS</span>
            </div>
          </div>

          <motion.h1
            className="font-anton text-5xl md:text-7xl uppercase tracking-tight text-center"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #00FF99 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="gallery-title"
          >
            Your Collection
          </motion.h1>

          {/* Stats */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 mt-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-[#121212] border border-white/10 rounded-lg px-6 py-3">
              <p className="font-space text-sm text-white/60 uppercase">Total Cards</p>
              <p className="font-anton text-3xl text-neon-pink" data-testid="total-count">{figures.length}</p>
            </div>
            {['common', 'rare', 'epic', 'legendary'].map((rarity) => (
              <button 
                key={rarity} 
                className={`bg-[#121212] border rounded-lg px-6 py-3 transition-all cursor-pointer ${
                  filterRarity === rarity ? 'border-2' : 'border-white/10 hover:border-white/30'
                }`}
                style={{ borderColor: filterRarity === rarity ? rarityColors[rarity] : undefined }}
                onClick={() => setFilterRarity(filterRarity === rarity ? 'all' : rarity)}
              >
                <p className="font-space text-sm text-white/60 uppercase">{rarity}</p>
                <p className="font-anton text-3xl" style={{ color: rarityColors[rarity] }}>
                  {rarityStats[rarity] || 0}
                </p>
              </button>
            ))}
          </motion.div>
          
          {/* Burn Info */}
          <div className="mt-6 text-center">
            <p className="font-space text-sm text-white/40">
              Click a card to share or burn. Burn rewards: 
              <span className="text-gray-400 ml-2">Common: 10</span>
              <span className="text-cyan-400 ml-2">Rare: 25</span>
              <span className="text-fuchsia-400 ml-2">Epic: 50</span>
              <span className="text-yellow-400 ml-2">Legendary: 100</span>
            </p>
          </div>

          {/* View Controls & Burn Duplicates */}
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            {/* Stack Toggle */}
            <Button
              onClick={() => setStackedView(!stackedView)}
              className={`border-2 font-space text-sm ${
                stackedView 
                  ? 'bg-cyan-600/20 border-cyan-500 text-cyan-400' 
                  : 'bg-transparent border-white/20 text-white/60'
              }`}
              data-testid="stack-toggle"
            >
              <Layers className="w-4 h-4 mr-2" />
              {stackedView ? 'Stacked View' : 'Show All'}
            </Button>

            {/* Burn Duplicates Button */}
            {duplicateStats.count > 0 && (
              <Button
                onClick={() => setShowBurnDuplicatesModal(true)}
                className="bg-orange-600/20 border-2 border-orange-500 text-orange-400 hover:bg-orange-600/40 font-space text-sm"
                data-testid="burn-duplicates-btn"
              >
                <Flame className="w-4 h-4 mr-2" />
                Burn {duplicateStats.count} Duplicates (~{duplicateStats.shards} Shards)
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Gallery Grid */}
      <main className="relative z-10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20" data-testid="loading-spinner">
              <Loader2 className="w-12 h-12 text-neon-pink animate-spin" />
              <p className="font-space text-white/60 mt-4">Loading your collection...</p>
            </div>
          ) : figures.length === 0 ? (
            <motion.div
              className="flex flex-col items-center justify-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="empty-collection"
            >
              <p className="font-anton text-3xl text-white/40 uppercase mb-4">No Figures Yet</p>
              <p className="font-space text-white/60 mb-8">Open mystery boxes to start your collection!</p>
              <Button
                onClick={goBack}
                className="bg-neon-pink hover:bg-neon-pink/80 text-white font-space uppercase"
              >
                Start Collecting
              </Button>
            </motion.div>
          ) : (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              data-testid="gallery-grid"
            >
              {filteredFigures.map((figure, index) => (
                <motion.div
                  key={figure.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative group"
                >
                  {/* Quantity Badge for Stacked View */}
                  {stackedView && figure.quantity > 1 && (
                    <motion.div
                      className="absolute -top-2 -right-2 z-30 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center border-2 border-white shadow-lg"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.05 + 0.1, type: 'spring' }}
                    >
                      <span className="font-anton text-lg text-white">x{figure.quantity}</span>
                    </motion.div>
                  )}

                  <EnhancedCard
                    card={figure}
                    onClick={() => handleFigureClick(figure)}
                    showNews={true}
                  />
                  
                  {/* Burn Button Overlay */}
                  <motion.button
                    className="absolute top-2 right-2 z-20 p-2 rounded-full bg-red-600/80 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCardToBurn(figure);
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{ top: stackedView && figure.quantity > 1 ? '2.5rem' : '0.5rem' }}
                    data-testid={`burn-btn-${figure.id}`}
                  >
                    <Flame className="w-5 h-5 text-white" />
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </main>

      {/* Share Modal */}
      {selectedFigure && (
        <ShareFlexModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedFigure(null);
          }}
          card={selectedFigure}
          onBurn={() => {
            setShowShareModal(false);
            setCardToBurn(selectedFigure);
          }}
        />
      )}
    </div>
  );
};

export default GalleryPage;
